export interface IntentPageSection {
  eyebrow?: string;
  title: string;
  body: string[];
  bullets?: string[];
}

export interface IntentPage {
  slug: string;
  title: string;
  metaTitle: string;
  description: string;
  eyebrow: string;
  hero: string;
  primaryCta?: string;
  sections: IntentPageSection[];
}

export const INTENT_PAGES: IntentPage[] = [
  {
    slug: 'oregon-truffle-festival-travel-guide',
    title: 'Oregon Truffle Festival Travel Guide',
    metaTitle: 'Oregon Truffle Festival Travel Guide | Events, Tickets, Hotels, and Trip Planning',
    description:
      'Plan an Oregon Truffle Festival weekend around McMinnville, Eugene, and Newberg events: what to expect, where to stay, how tickets work, and which events fit your trip.',
    eyebrow: 'Festival travel',
    hero:
      'The Oregon Truffle Festival is a winter event series that spreads across the Willamette Valley and Eugene. It is a strong travel anchor, but the right trip depends on picking the right events, staying close to the action, and not over-scheduling.',
    primaryCta: 'Check the current-year schedule and buy tickets directly from the Oregon Truffle Festival before booking non-refundable travel.',
    sections: [
      {
        eyebrow: 'What it is',
        title: 'What the Oregon Truffle Festival actually is',
        body: [
          'The Oregon Truffle Festival is a nonprofit organization that has run late-winter truffle events since 2006. It is not a single ticket or a single venue; it is a series of events that moves between Eugene, McMinnville, Newberg, and other valley locations.',
          'Some events are spectator-friendly, some are hands-on classes or training, and some are multi-course dinners or wine-pairing experiences. Each event is usually ticketed separately.',
        ],
      },
      {
        eyebrow: 'Events',
        title: 'The main events travelers should know about',
        body: [
          'The festival calendar changes every year, but a few event types show up reliably. The 2026 examples below are from official festival listings; always confirm the current year\'s schedule before booking.',
        ],
        bullets: [
          'Joriad North American Truffle Dog Championship (Eugene): a spectator-friendly amateur dog competition. Listed for 2026 at Lane Events Center.',
          'Fresh Truffle Marketplace (McMinnville): a tasting and purchase event with fresh truffles, regional wines, and vendor samples. Listed for 2026 at The Ground at Stillwater.',
          'Two-Day Truffle Dog Training (west of Eugene): handler-and-dog field instruction, usually late February into March. Confirm whether it is open to spectators or handler-dog teams only.',
          'Cooking classes and foray experiences: multi-day or one-day hands-on cooking and foraging workshops around Eugene and the valley. Space is often limited.',
          'Gala dinners and wine-pairing events: multi-course truffle dinners in the Willamette Valley. Menus and venues change by year.',
        ],
      },
      {
        eyebrow: 'Where to stay',
        title: 'How to choose a festival base',
        body: [
          'Eugene is the best base for the Joriad and dog-training weekends. McMinnville and Newberg are better for the Fresh Truffle Marketplace, valley forays, and gala dinners. Some events include shuttles from partner hotels; others require you to reach a central meetup point.',
          'Rooms get tight during festival weekends, so book lodging at the same time you buy tickets.',
        ],
        bullets: [
          'Eugene: Inn at the 5th, The Gordon Hotel, and Graduate by Hilton (a past festival lodging partner).',
          'Willamette Valley: Atticus Hotel in McMinnville and The Allison Inn & Spa in Newberg put you near the marketplace, valley forays, and dining.',
        ],
      },
      {
        eyebrow: 'Tickets',
        title: 'How tickets and shuttles usually work',
        body: [
          'Most events are sold separately. Some include food, wine, transportation to private foray sites, or a take-home item; others are admission-only. Read the event page carefully before buying.',
          'The festival sometimes offers lodging partner rates and shuttle service from designated hotels. These details change yearly, so verify before assuming a shuttle is available.',
          'Spectator events like the Joriad are easier to attend. Hands-on classes, forays, and training weekends may have limited space and fill early.',
        ],
      },
      {
        eyebrow: 'Trip shape',
        title: 'Build the weekend around one or two events',
        body: [
          'A good festival weekend is not about attending everything. Pick one main event, add one dinner reservation, and leave time between venues. Eugene and McMinnville are roughly 1.5 to 2 hours apart by car, so avoid booking same-day morning and evening events in both places.',
          'If you want to actually hunt, pair a festival ticket with a guided hunt. If you want to taste and learn without entering the woods, the marketplace and cooking classes are a better fit.',
        ],
      },
      {
        eyebrow: 'Non-festival stops',
        title: 'What to do on either side of a festival day',
        body: [
          'In the Willamette Valley, you can add a guided hunt with Black Tie Tours, a meal at JORY or The Joel Palmer House, or a stay at Atticus or The Allison.',
          'In Eugene, the 5th Street Public Market and nearby restaurants fit a festival weekend. In Portland, Truffle Connection PNW offers forays and the PSU Farmers Market truffle stand runs on winter Saturdays.',
          'These stops are independent of the festival; verify hours and availability around your festival dates.',
        ],
      },
      {
        eyebrow: 'Before you book',
        title: 'Festival cautions',
        body: [
          'Event dates, venues, prices, and ticket packages change every year. Some forays take place on private land and do not allow private vehicles; shuttle requirements and meetup locations are released with tickets.',
          'Oregon\'s winter truffle season is cold, wet, and muddy. Build buffer into your schedule and confirm cancellation and weather policies.',
        ],
        bullets: [
          'Confirm current-year dates and ticket types on the official festival site.',
          'Book lodging before or alongside event tickets.',
          'Read cancellation, weather, and shuttle policies before buying non-refundable travel.',
        ],
      },
    ],
  },
  {
    slug: 'truffle-hunting-in-oregon',
    title: 'Truffle Hunting in Oregon',
    metaTitle: 'Truffle Hunting in Oregon | Guided Tours, Dog-Led Outings, and Beginner Trip Planning',
    description:
      'A safe beginner guide to Oregon truffle hunting: what to expect on a guided hunt, why dogs matter, how to book responsibly, and how to plan around access, season, and etiquette.',
    eyebrow: 'Guided hunting guide',
    hero:
      'Oregon truffle hunting is easiest to plan as a guided, dog-led, seasonal experience. The best trips pair a verified operator with realistic expectations about access, weather, and what you will actually do in the forest.',
    primaryCta: 'Book directly with the operator and confirm dates, inclusions, land access, and cancellation policies before you travel.',
    sections: [
      {
        eyebrow: 'The experience',
        title: 'What a guided Oregon truffle hunt is really like',
        body: [
          'A typical guided hunt starts with a meetup in town or at a trailhead, then a short drive to a private or permitted truffle-producing stand of Douglas fir.',
          'Your guide introduces a trained dog team, explains how to read canine alerts, and lets the dogs locate ripe truffles under the duff. You may get to dig, learn to handle and clean the truffle, and sometimes take one home. Some tours also include a field lunch, wine pour, or demo back at the trailhead.',
          'It is not a treasure hunt with guaranteed finds. Ripeness, weather, dog focus, and patch condition all affect the day, so the best operators set clear expectations up front.',
        ],
      },
      {
        eyebrow: 'Dogs vs rakes',
        title: 'Why most travelers should hunt with a dog, not a rake',
        body: [
          'Dogs can smell ripe truffles and leave unripe ones in the ground. That protects future harvests and usually yields better culinary quality.',
          'Raking tears up the duff and root layer, indiscriminately exposes unripe and overripe truffles, and is increasingly restricted on public land. Low-impact harvesting is part of what keeps Oregon truffle quality credible.',
          'If you want to bring your own dog, ask the operator whether your dog is a fit and whether the booking allows it. Most guided experiences include a trained dog team as part of the trip.',
        ],
        bullets: ['Do not trespass.', 'Do not rake or dig without permission.', 'Do not assume a public mention of a forest means open visitor access.'],
      },
      {
        eyebrow: 'What to ask',
        title: 'What to know before you book a hunt',
        body: [
          'Most public guided hunts run in the winter season, roughly January through March, with some operators offering private or spring options.',
          'Ask what is included: transportation, dog team, instruction, digging time, tasting, lunch, wine, and whether you keep any truffles. Also ask about group size, physical demands, weather cancellation policy, and when the exact meeting location is released.',
          'Ask whether the experience is suitable for children, dogs, or travelers with mobility limits. Some sites are rougher and wetter than others.',
        ],
        bullets: [
          'Confirm the operator\'s current season and availability directly on their site.',
          'Verify land access or permits if you are not booking through a known guide.',
          'Read cancellation and weather policies before buying tickets or non-refundable lodging.',
        ],
      },
      {
        eyebrow: 'Operator options',
        title: 'Verified guided-hunt operators in the Oregon guide',
        body: [
          'These operators appear in the guide because they currently describe guided, seasonal truffle experiences with dog teams and booking on their official sites. Use this list as a starting point and verify dates, availability, and exact offerings before booking.',
        ],
        bullets: [
          'Black Tie Tours (Newberg): Bougie and Rustic Truffle Tromp options, usually late January through March. Pickup in downtown Newberg, forest hunt, lunch, optional wine; private tours available by inquiry.',
          'Truffle Connection PNW Forays (Portland / Estacada): Small-group day trips from Portland to Estacada-area forest. Includes dog demo, hunt, and regional food/wine; seasonal Saturdays and scheduled dates.',
          'Truffle Dog Company Private Adventures (Willamette Valley): Private adventures and spring field instruction, listed from December through May. Best for travelers who want a quieter, by-appointment hunt or training weekend.',
        ],
      },
      {
        eyebrow: 'Dog training',
        title: 'Can you train your own dog to hunt Oregon truffles?',
        body: [
          'Some operators and organizations offer truffle-dog training, but it is a specialized skill that requires scent work, field practice, and a suitable dog.',
          'The Oregon Truffle Festival lists two-day training weekends west of Eugene, and regional societies and trainers offer additional classes. If you are visiting purely to experience a hunt, joining a guide with a trained dog is usually the better use of a short trip. Training your own dog is a longer project.',
        ],
      },
      {
        eyebrow: 'Rules of the forest',
        title: 'Access and etiquette for Oregon truffle hunters',
        body: [
          'Many productive truffle patches are on private timberland, farms, or restricted public land. A guided operator handles access; independent hunters must get explicit permission and any required permits.',
          'The Bureau of Land Management in some northwest Oregon areas requires dog-assisted harvesting and prohibits raking. Check with the local BLM or forest manager before hunting on public land.',
          'Replace duff and litter after digging, do not damage host-tree roots, pack out all trash, and leave unripe truffles in place. Keep dogs under voice or leash control and away from roads, streams, and other forest users unless otherwise directed.',
        ],
        bullets: ['Trespassing is not a shortcut.', 'Raking can damage habitat and future truffle patches.', 'A fruiting window does not equal public access.'],
      },
      {
        eyebrow: 'Where to start',
        title: 'Which part of Oregon fits your hunt',
        body: [
          'Portland / Estacada works best for city-base day trips with Portland pickup and quick foothill access.',
          'Willamette Valley (Newberg, McMinnville, Dayton) is the classic weekend shape, combining guides, resort dining, and boutique stays.',
          'Eugene is best for festival-linked events and two-day dog-training weekends, and Corvallis is best for society meetings, educational talks, and the research side of truffle culture.',
          'These are travel bases, not exact forest coordinates. Your operator or host chooses the site based on current conditions and permission.',
        ],
      },
      {
        eyebrow: 'Gear',
        title: 'What to bring on an Oregon truffle hunt',
        body: [
          'Dress for cold, wet weather: waterproof boots, rain pants or gaiters, warm layers, gloves, and a hat.',
          'Bring water, snacks, a small breathable container for any truffles you take home, and a phone or camera. Most guides provide digging tools and instruction. If you are going independently, carry only light hand tools and only on land where you have permission.',
          'If bringing your own dog, bring a leash, high-value treats, water, and waste bags.',
        ],
      },
    ],
  },
  {
    slug: 'oregon-truffle-season',
    title: 'Oregon Truffle Season',
    metaTitle: 'Oregon Truffle Season | Winter White, Spring White, Black, and Brown Truffle Months',
    description:
      'Plan Oregon truffle travel around real species seasons: winter white, spring white, black, and brown truffle timing, plus festival dates, guided-tour windows, and booking lead times.',
    eyebrow: 'Season guide',
    hero:
      'Oregon truffle season is not one month. Different native species fruit at different times, and the public-event calendar is concentrated in winter. Use this page to match the season to the trip you want.',
    primaryCta: 'Use these ranges as planning context, then confirm the current year\'s events, tours, and weather before booking.',
    sections: [
      {
        eyebrow: 'Species calendar',
        title: 'Oregon truffle species and their general fruiting windows',
        body: [
          'Oregon has several native truffle species, but the travel conversation usually centers on winter white, spring white, black, and brown truffles.',
          'Public extension and operator guidance commonly frames the fruiting windows this way. Exact timing varies by rainfall, temperature, forest site, and elevation.',
        ],
        bullets: [
          'Oregon winter white: generally October through February, with the strongest visitor calendar from January through March.',
          'Oregon spring white: generally January through June, with a quieter, more private-trip feel from March through May.',
          'Oregon black: generally September through February, overlapping with winter white in the early part of the year.',
          'Oregon brown: generally September through February, with a similar late-fall-to-winter window.',
        ],
      },
      {
        eyebrow: 'Trip timing',
        title: 'How season shapes your Oregon truffle trip',
        body: [
          'Winter is the easiest first trip because public tours, the Oregon Truffle Festival, restaurant truffle programming, and general visitor infrastructure are easier to find and verify.',
          'Late winter and early spring can work well for quieter, smaller-group private hunts and training weekends, especially for travelers who are comfortable booking by appointment and dealing with a less predictable calendar.',
          'Fall can be biologically active for black and brown truffles, but public tours and festival programming are usually thinner before the main winter season.',
        ],
        bullets: [
          'Winter: best for first-time visitors and festival-driven travel.',
          'Early spring: best for private outings, dog-training weekends, and repeat travelers who want lower crowds.',
          'Late spring/summer: not the main public season; any summer trip should be treated as a bespoke, by-appointment option only.',
        ],
      },
      {
        eyebrow: 'Festival anchors',
        title: 'Public Oregon truffle events that anchor the season',
        body: [
          'Several recurring events create the most reliable winter travel anchors. Dates and venues change each year, so confirm before booking lodging or flights.',
        ],
        bullets: [
          'Oregon Truffle Festival: multi-day events across the Willamette Valley and Eugene, usually February; includes the Fresh Truffle Marketplace in McMinnville and the Joriad championship in Eugene.',
          'Joriad North American Truffle Dog Championship: a Eugene spectator event held during the festival window; useful for dog-focused travel.',
          'Two-day Truffle Dog Training: Oregon Truffle Festival programming west of Eugene, typically late February into March; confirm dog-team eligibility and spectator versus handler access.',
          'PSU Portland Farmers Market Truffle Stand: seasonal Saturday appearances in January and February from Truffle Connection PNW; a lower-commitment city stop.',
          'NATS Annual Potluck Dinner and public meetings: Corvallis-area gatherings for education-minded travelers; usually February and ongoing through the society calendar.',
        ],
      },
      {
        eyebrow: 'Booking windows',
        title: 'When to book a guided hunt or festival trip',
        body: [
          'Most guided group hunts and festival events open tickets in late fall or early winter for the January-March window.',
          'Private outings and training weekends may have more flexible lead times but still depend on operator schedules and current conditions.',
          'If you are traveling from out of state, book lodging before or alongside event tickets; McMinnville, Newberg, and Eugene rooms tighten during festival weekends.',
        ],
        bullets: [
          'Group tours: book 2-8 weeks ahead during peak winter season.',
          'Festival tickets: monitor the Oregon Truffle Festival site for release dates and venue changes.',
          'Private trips: confirm 2-4 weeks ahead, or earlier for spring shoulder weekends.',
        ],
      },
      {
        eyebrow: 'Weather and conditions',
        title: 'What the season feels like on the ground',
        body: [
          'Oregon truffle season is cold, wet, and muddy. Rain is normal, not exceptional. Forest floors can be slick and saturated, and valley and foothill temperatures often sit in the 30s and 40s Fahrenheit in winter, with spring warming slowly.',
          'Some operators may cancel or relocate for severe weather or for animal damage to a truffle patch. Build buffer into your itinerary and ask about cancellation terms.',
        ],
        bullets: [
          'Expect rain and mud; waterproof boots and rain gear are essential.',
          'Daylight hours are short in winter; most hunts start mid-morning and finish by mid-afternoon.',
          'Spring can be milder but also less predictable for volume and public events.',
        ],
      },
      {
        eyebrow: 'Cautions',
        title: 'Season does not mean open access',
        body: [
          'A species fruiting window tells you when truffles may be mature, not where you are allowed to look for them. Access, permits, operator availability, and land rules still determine what a visitor can actually do.',
          'Use these season ranges to choose a month and trip type, then confirm the specific event, tour, or private booking directly with the operator or land manager.',
        ],
        bullets: [
          'Do not trespass or rake on private or public land without permission.',
          'Do not assume a fruiting season applies statewide; microclimates and forest conditions vary.',
          'Verify current-year dates before buying non-refundable travel.',
        ],
      },
    ],
  },
  {
    slug: 'where-to-find-truffles-in-oregon',
    title: 'Where to Find Truffles in Oregon',
    metaTitle: 'Where to Find Truffles in Oregon | Regions, Events, and Guided Trip Bases',
    description:
      'Find Oregon truffles legally and safely through guided hunts, festivals, markets, and restaurant stops in the Willamette Valley, Eugene, Portland, and Corvallis — not by foraging on your own.',
    eyebrow: 'Region guide',
    hero:
      'The most honest answer to "where do I find Oregon truffles?" is: on a guided hunt, at a festival or market, or on a plate at a restaurant. Wild patches are mostly on private or restricted land, so the guide shows you where to look without risking trespass or habitat damage.',
    primaryCta: 'Use public events, guided operators, and dining stops to experience Oregon truffles. Confirm access and permits before any independent foray.',
    sections: [
      {
        eyebrow: 'The short answer',
        title: 'Where a visitor can actually find Oregon truffles',
        body: [
          'Guided hunts in the Willamette Valley, Portland foothills, and Eugene area are the main legal, safe way to find truffles in the ground.',
          'Festivals like the Oregon Truffle Festival and farmers-market stands like Truffle Connection PNW at PSU offer fresh truffles and tastings without entering the woods.',
          'Restaurant stops such as JORY, The Joel Palmer House, Cypress, and Marché put truffles on the plate.',
          'Independent foraging on public land requires permits and current rules; most productive patches are on private land with no public access.',
        ],
      },
      {
        eyebrow: 'Regions',
        title: 'Oregon truffle regions for trip planning',
        body: [
          'Oregon truffles are found in western Oregon, especially in Douglas-fir forests in the Coast Range foothills and valley edges. These are travel bases, not exact spots.',
        ],
        bullets: [
          'Willamette Valley (Newberg, McMinnville, Dayton): the classic truffle weekend. Guided hunts with Black Tie Tours and Truffle Dog Company, the Fresh Truffle Marketplace, and dining at JORY and The Joel Palmer House.',
          'Portland / Estacada foothills: city-base day trips with Truffle Connection PNW forays and the PSU Farmers Market truffle stand.',
          'Eugene: festival and dog-event anchor. Oregon Truffle Festival Joriad and two-day truffle-dog training weekends.',
          'Corvallis: the education and society side. NATS public meetings and the annual potluck near the OSU campus.',
        ],
      },
      {
        eyebrow: 'Why guided',
        title: 'Why guided hunts are the best way to find truffles',
        body: [
          'Guides have land access, trained dogs, and knowledge of ripeness. Dogs leave unripe truffles in place; raking tears up the duff and root layer and is increasingly restricted on public land.',
          'You may get to keep a truffle or buy from the guide. Ask what is included before booking.',
        ],
      },
      {
        eyebrow: 'Habitat',
        title: 'What kind of places produce Oregon truffles',
        body: [
          'Native Oregon truffles grow in Douglas-fir forests in western Oregon, from the Coast Range foothills above the Willamette Valley to lowland forests west of the Cascades.',
          'They prefer cool, rainy winters, loamy soils, and shaded understory with moss and ferns. That does not mean every Douglas-fir patch is open to visitors; most productive areas are on private or restricted land.',
        ],
      },
      {
        eyebrow: 'No coordinates',
        title: 'Why exact locations are not listed here',
        body: [
          'Productive truffle patches are usually on private property or leased land. Sharing exact coordinates would encourage trespass and raking damage.',
          'Reputable operators do not publish exact sites. They release meeting points one to two weeks ahead, based on current conditions and permission. If an event description says "secret forest," that is normal and responsible.',
        ],
      },
      {
        eyebrow: 'Dining option',
        title: 'The easiest way to taste truffles without a hunt',
        body: [
          'The Fresh Truffle Marketplace in McMinnville, the PSU Farmers Market truffle stand in Portland, and restaurant menus at JORY, The Joel Palmer House, Cypress at Atticus, and Marché are the most accessible ways to taste Oregon truffles.',
          'Seasonal availability and menu items change, so call ahead or check current menus before planning a meal around truffles.',
        ],
      },
      {
        eyebrow: 'Cautions',
        title: 'Do not treat a region name as permission',
        body: [
          'A region may produce truffles, but you still need landowner permission or permits to forage. Bureau of Land Management and national forest lands in some northwest Oregon areas require dog-assisted harvesting and prohibit raking.',
          'Trespassing and unpermitted digging can damage habitat, hurt future truffle crops, and expose you to liability.',
        ],
      },
    ],
  },
];

export function getIntentPageBySlug(slug: string): IntentPage | undefined {
  return INTENT_PAGES.find((page) => page.slug === slug);
}
